import React, { useEffect, useState, useCallback } from "react";
import BlendedNominationService from "../Service/BlendedNominationService";
import BlendedOfferingsService from "../Service/BlendedOfferingsService";
import { useFreezeNomination } from "../Context/FreezeNominationContext";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";

function BlendedNominationFinalization() {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [blendedPrograms, setBlendedPrograms] = useState([]);
  const [selectedConnects, setSelectedConnects] = useState({}); // Store selected SMEs for each connect
  const [expandedPrograms, setExpandedPrograms] = useState({}); // Track which programs are expanded
  const [selectedPrograms, setSelectedPrograms] = useState(new Set()); // Track selected programs for finalization
  const { isFrozen, toggleFreezeNomination } = useFreezeNomination();
  const [sortCriteria, setSortCriteria] = useState("nominationTime");

  const location = useLocation();
  const { user } = location.state || {};

  // Function to get program status from backend
  const getProgramStatus = useCallback((program) => {
    return program.status;
  }, []);

  useEffect(() => {
    if (selectedMonth !== "" && user?.track) {
      fetchBlendedProgramsByMonthAndTrack();
    }
  }, [selectedMonth, user?.track]);

  const fetchBlendedProgramsByMonthAndTrack = useCallback(async () => {
    if (!selectedMonth || !user?.track) {
      setBlendedPrograms([]);
      setShowTable(false);
      return;
    }
    try {
      const response = await BlendedOfferingsService.getProgramsByTrackAndMonth(
        user.track,
        selectedMonth
      );
      if (response.length !== 0) {
        setBlendedPrograms(response);
        setShowTable(true);
      } else {
        setBlendedPrograms([]);
        setShowTable(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setShowTable(false);
    }
  }, [selectedMonth, user]);

  // Group programs and their modules with nominated SMEs
  const groupProgramsByModulesAndConnects = (programs) => {
    const groupedPrograms = {};

    programs.forEach((program) => {
      const programKey = `${program.blendedProgramOfferingId}-${program.blendedProgramName}`;
      
      if (!groupedPrograms[programKey]) {
        groupedPrograms[programKey] = {
          blendedProgramOfferingId: program.blendedProgramOfferingId,
          blendedProgramName: program.blendedProgramName,
          programWDCourseCode: program.programWDCourseCode,
          track: program.track,
          publishedDate: program.publishedDate,
          status: program.status,
          modules: {},
        };
      }

      program.modules.forEach((module) => {
        const moduleKey = `${module.moduleId}-${module.moduleName}`;
        
        if (!groupedPrograms[programKey].modules[moduleKey]) {
          groupedPrograms[programKey].modules[moduleKey] = {
            moduleId: module.moduleId,
            moduleName: module.moduleName,
            moduleWDCourseCode: module.moduleWDCourseCode,
            description: module.description,
            duration: module.duration,
            numOfSMEConnects: module.numOfSMEConnects,
            assessmentDate: module.assessmentDate,
            quizDate: module.quizDate,
            connects: {},
          };
        }

        module.connects.forEach((connect) => {
          const connectKey = `${connect.connectId}`;
          
          if (!groupedPrograms[programKey].modules[moduleKey].connects[connectKey]) {
            groupedPrograms[programKey].modules[moduleKey].connects[connectKey] = {
              connectId: connect.connectId,
              date: connect.date,
              time: connect.time,
              day: connect.day,
              nominations: connect.nominations || [],
              collectiveStatus: connect.collectiveStatus || "NOT YET CONFIRMED",
              finalizedSME: connect.finalizedSME,
            };
          }
        });
      });
    });

    return Object.values(groupedPrograms).map((program) => ({
      ...program,
      modules: Object.values(program.modules).map((module) => ({
        ...module,
        connects: Object.values(module.connects),
      })),
    }));
  };



  // Toggle program expansion
  const toggleProgramExpansion = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };

  // Handle program selection for finalization
  const handleProgramSelection = (programId, isSelected) => {
    setSelectedPrograms(prev => {
      const updated = new Set(prev);
      if (isSelected) {
        updated.add(programId);
      } else {
        updated.delete(programId);
      }
      return updated;
    });
  };

  // Helper function to get selected SME email from stored JSON
  const getSelectedSMEEmail = (absoluteUniqueKey) => {
    const selectionValue = selectedConnects[absoluteUniqueKey];
    if (!selectionValue) return "";
    
    try {
      const selectionData = JSON.parse(selectionValue);
      return JSON.stringify({
        smeEmail: selectionData.smeEmail,
        smeNominationId: selectionData.smeNominationId
      });
    } catch (error) {
      return "";
    }
  };

  // Handle selection change for dropdown
  const handleSelectionChange = (programId, moduleIdx, moduleId, connectIdx, connectId, selectionValue) => {
    const absoluteUniqueKey = `prog-${programId}-mod-${moduleIdx}-modid-${moduleId}-conn-${connectIdx}-${connectId || 'no-id'}`;
    
    setSelectedConnects((prev) => ({
      ...prev,
      [absoluteUniqueKey]: selectionValue,
    }));
  };

  // Handle Finalize Nomination button click
  const handleFinalizeNominations = async () => {
    if (selectedPrograms.size === 0) {
      alert("Please select at least one program to finalize.");
      return;
    }

    try {
      // Filter finalization data to only include selected programs
      const finalizationData = Object.entries(selectedConnects)
        .filter(([key, selectionValue]) => {
          // Extract programId from the new key format: prog-{programId}-mod-{moduleIdx}-modid-{moduleId}-conn-{connectIdx}-{connectId}
          const match = key.match(/^prog-([^-]+)-mod-/);
          const programId = match ? match[1] : null;
          return programId && selectedPrograms.has(programId) && selectionValue;
        })
        .map(([key, selectionValue]) => {
          // Updated regex to handle UUID format in moduleId and connectId
          const keyMatch = key.match(/^prog-([^-]+)-mod-(\d+)-modid-([a-f0-9-]+)-conn-(\d+)-(.+)$/);
          
          if (!keyMatch) return null;
          
          const [, programId, moduleIdx, moduleId, connectIdx, connectId] = keyMatch;
          const actualConnectId = connectId === 'no-id' ? connectIdx : connectId;
          
          let smeEmail, smeNominationId;
          try {
            const selectionData = JSON.parse(selectionValue);
            smeEmail = selectionData.smeEmail;
            smeNominationId = selectionData.smeNominationId;
          } catch (error) {
            return null;
          }
          
          return {
            blendedProgramOfferingId: programId,
            moduleId: moduleId,
            connectId: actualConnectId,
            smeNominationId: smeNominationId,
            selectedSMEEmail: smeEmail,
          };
        })
        .filter(item => item !== null);

      if (finalizationData.length === 0) {
        alert("Please select SMEs for the selected programs before finalizing.");
        return;
      }

      const response = await BlendedNominationService.finalizeBlendedNomination(
        selectedMonth,
        finalizationData
      );
      
      // Get program names for the selected programs
      const selectedProgramNames = blendedPrograms
        .filter(program => selectedPrograms.has(program.blendedProgramOfferingId))
        .map(program => program.blendedProgramName);
      
      if (response && response.length > 0) {
        const programNamesText = selectedProgramNames.map(name => `• ${name}`).join('\n');
        alert(`The following programs finalized successfully:\n\n${programNamesText}`);
      } else {
        alert("Failed to finalize nominations. Please try again.");
      }

      fetchBlendedProgramsByMonthAndTrack();
      setSelectedConnects({});
      setSelectedPrograms(new Set());
    } catch (error) {
      console.error("Error finalizing nomination:", error);
      alert("Failed to finalize nominations. Please try again.");
    }
  };



  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
        Blended Nominations Finalization Page
      </h2>
      <div className="flex flex-col items-center justify-center mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="month">
          Select Month
        </label>
        <input
          id="month"
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="w-60 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          required
        />
      </div>

      {showTable && blendedPrograms.length > 0 && (
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center space-x-6 mb-4 bg-white p-4 rounded-lg shadow-md">
            {/* Freeze Nomination Toggle */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isFrozen}
                onChange={toggleFreezeNomination}
                className="hidden"
              />
              <div
                className={`relative w-12 h-6 rounded-full ${
                  isFrozen ? "bg-red-500" : "bg-green-500"
                }`}
              >
                <div
                  className={`absolute top-0 left-0 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-200 ${
                    isFrozen ? "transform translate-x-6" : ""
                  }`}
                ></div>
              </div>
              <span className="ml-2 text-black">
                {isFrozen ? "Toggle to Unfreeze for Nominations" : "Toggle to Freeze Nominations"}
              </span>
            </label>

            {/* Sort Criteria Dropdown */}
            <div className="flex items-center space-x-2">
              <label className="text-gray-700" htmlFor="sort">
                Select Sort Criteria for Nominated SMEs:
              </label>
              <select
                className="px-4 py-2 border rounded-md focus:outline-none"
                value={sortCriteria}
                onChange={(e) => setSortCriteria(e.target.value)}
              >
                <option value="nominationTime">Nomination Time</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md border border-gray-300 overflow-hidden mb-8">
            {/* Add notification for cancelled nominations */}
            {blendedPrograms.some(program => program.status === 'NOMINATION CANCELLED') && (
              <div className="bg-orange-100 border-l-4 border-orange-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-orange-700">
                      <strong>Warning:</strong> Some finalized programs have confirmed SMEs who have cancelled their nominations. 
                      These programs are marked as "NOMINATION CANCELLED" and cannot be finalized until replacements are found.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-purple-500 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                Blended Programs for Finalization - {user?.track || 'Your Track'} ({selectedMonth ? new Date(selectedMonth).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Selected Month'})
                {isFrozen && <span className="ml-3 text-sm bg-red-600 px-2 py-1 rounded">🔒 NOMINATIONS FROZEN</span>}
              </h3>
              <span className="text-sm bg-purple-600 px-3 py-1 rounded">
                {selectedPrograms.size} selected for finalization
              </span>
            </div>

            <div className="divide-y divide-gray-200">
              {blendedPrograms.map((program) => {
                const programStatus = getProgramStatus(program);
                return (
                <div key={program.blendedProgramOfferingId} className="bg-white">
                  <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleProgramExpansion(program.blendedProgramOfferingId)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <svg
                          className={`w-5 h-5 transform transition-transform ${
                            expandedPrograms[program.blendedProgramOfferingId] ? "rotate-90" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedPrograms.has(program.blendedProgramOfferingId)}
                          onChange={(e) => handleProgramSelection(program.blendedProgramOfferingId, e.target.checked)}
                          disabled={programStatus === 'FINALIZED'}
                          className={`w-4 h-4 border-gray-300 rounded focus:ring-purple-500 ${
                            programStatus === 'FINALIZED' 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-purple-600'
                          }`}
                          title={
                            programStatus === 'FINALIZED' 
                              ? 'This program is already finalized and cannot be selected' 
                              : programStatus === 'NOMINATION CANCELLED' 
                                ? 'Select to finalize with a different SME' 
                                : 'Select program for finalization'
                          }
                        />
                        <div>
                          <h4 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                            <span>{program.blendedProgramName}</span>
                            {programStatus === 'FINALIZED' && (
                              <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded">✓ FINALIZED</span>
                            )}
                            {programStatus === 'PUBLISHED' && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">PUBLISHED</span>
                            )}
                            {programStatus === 'NOMINATION CANCELLED' && (
                              <span className="text-xs bg-orange-600 text-white px-2 py-1 rounded">⚠ NOMINATION CANCELLED</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Code: {program.programWDCourseCode} | Track: {program.track} | {program.modules.length} modules | Instance: {program.blendedProgramOfferingId}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedPrograms[program.blendedProgramOfferingId] && (
                    <div className="px-6 pb-6">
                      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                        {program.modules.map((module, moduleIdx) => (
                          <div key={`${module.moduleId}-${moduleIdx}`} className="mb-4 border-b border-gray-200 last:border-b-0 pb-4 last:pb-0">
                            <div className="bg-gray-100 px-4 py-2 border-l-4 border-purple-400">
                              <h5 className="text-md font-semibold text-gray-800">
                                {module.moduleName} ({module.moduleWDCourseCode})
                              </h5>
                              <p className="text-xs text-gray-600">
                                Duration: {module.duration} hours
                              </p>
                              {module.description && (
                                <p className="text-xs text-gray-600 italic">{module.description}</p>
                              )}
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-gray-200 text-gray-600">
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-32">Date</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-20">Day</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-24">Time</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-64">Nominated SMEs</th>
                                    <th className="px-3 py-2 text-left font-semibold text-sm border-b w-32">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {module.connects.map((connect, connectIdx) => {
                                    // Create unique selection key
                                    const absoluteUniqueKey = `prog-${program.blendedProgramOfferingId}-mod-${moduleIdx}-modid-${module.moduleId}-conn-${connectIdx}-${connect.connectId || 'no-id'}`;
                                    
                                    return (
                                      <tr key={`${connect.connectId || connectIdx}-${connectIdx}`} className="hover:bg-gray-50 border-b border-gray-200">
                                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                                          {format(new Date(connect.date), "dd-MMM-yyyy")}
                                        </td>
                                        <td className="px-3 py-2 text-sm">{connect.day}</td>
                                        <td className="px-3 py-2 text-sm">{connect.time}</td>
                                        <td className="px-3 py-2 text-sm">
                                          <select
                                            className={`w-full px-2 py-1 border rounded-md text-sm ${
                                              connect.finalizedSME ? 'bg-green-50 border-green-300' : ''
                                            }`}
                                            onChange={(e) => {
                                              // Prevent changes if already finalized
                                              if (connect.finalizedSME) {
                                                return;
                                              }
                                              handleSelectionChange(
                                                program.blendedProgramOfferingId,
                                                moduleIdx,
                                                module.moduleId,
                                                connectIdx,
                                                connect.connectId,
                                                e.target.value
                                              );
                                            }}
                                            value={
                                              connect.finalizedSME ? 
                                                JSON.stringify({
                                                  smeEmail: connect.finalizedSME,
                                                  smeNominationId: (connect.nominations || [])
                                                    .find(n => n.smeEmail === connect.finalizedSME)?.smeNominationId
                                                }) : 
                                                getSelectedSMEEmail(absoluteUniqueKey)
                                            }
                                            title={connect.finalizedSME ? "This connect has been finalized. Click to view all nominated SMEs." : "Select SME for this connect"}
                                          >
                                            <option value="">--Select SME--</option>
                                            {connect.finalizedSME && (
                                              <option
                                                value={JSON.stringify({
                                                  smeEmail: connect.finalizedSME,
                                                  smeNominationId: (connect.nominations || [])
                                                    .find(n => n.smeEmail === connect.finalizedSME)?.smeNominationId
                                                })}
                                                className="bg-green-100 font-semibold"
                                              >
                                                ✓ {connect.finalizedSME} (CONFIRMED)
                                              </option>
                                            )}
                                            {(connect.nominations || [])
                                              .filter(
                                                (nomination) =>
                                                  nomination.status !== "CANCELLED" &&
                                                  nomination.status !== "OPTED_OUT" &&
                                                  nomination.smeEmail !== connect.finalizedSME
                                              )
                                              .sort((a, b) => {
                                                // First, prioritize REPLACEMENT status nominations (alternative SMEs provided after cancellation)
                                                if (a.status === "REPLACEMENT" && b.status !== "REPLACEMENT") {
                                                  return -1;
                                                }
                                                if (b.status === "REPLACEMENT" && a.status !== "REPLACEMENT") {
                                                  return 1;
                                                }
                                                // Then sort by the selected criteria
                                                if (sortCriteria === "nominationTime") {
                                                  return new Date(a.nominationTime) - new Date(b.nominationTime);
                                                }
                                                return 0;
                                              })
                                              .map((nomination, nomIdx) => (
                                                <option
                                                  key={`${absoluteUniqueKey}-nom-${nomination.smeNominationId || nomIdx}`}
                                                  value={JSON.stringify({
                                                    smeEmail: nomination.smeEmail,
                                                    smeNominationId: nomination.smeNominationId
                                                  })}
                                                  className={nomination.status === "REPLACEMENT" ? "bg-yellow-50 font-medium" : ""}
                                                >
                                                  {nomination.smeEmail}
                                                  {nomination.status === "REPLACEMENT" && " (REPLACEMENT)"}
                                                </option>
                                              ))}
                                          </select>
                                        </td>
                                        <td className="px-3 py-2 text-sm">
                                          <span className={`px-2 py-1 rounded-full text-xs ${
                                            connect.collectiveStatus === "CONFIRMED" ? "bg-green-100 text-green-800" :
                                            connect.collectiveStatus === "CANCELLED" ? "bg-red-100 text-red-800" :
                                            connect.finalizedSME ? "bg-blue-100 text-blue-800" :
                                            "bg-yellow-100 text-yellow-800"
                                          }`}>
                                            {connect.finalizedSME ? "CONFIRMED" : connect.collectiveStatus}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {(module.assessmentDate || module.quizDate) && (
                              <div className="px-4 py-2 bg-gray-100 text-xs text-gray-600 space-y-1">
                                {module.assessmentDate && (
                                  <p>Assessment Date: {format(new Date(module.assessmentDate), "dd-MMM-yyyy")}</p>
                                )}
                                {module.quizDate && (
                                  <p>Quiz Date: {format(new Date(module.quizDate), "dd-MMM-yyyy")}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 bg-white p-4 rounded-lg shadow-md">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedPrograms.size === 0
                  ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  : 'bg-purple-500 text-white hover:bg-purple-600'
              }`}
              onClick={handleFinalizeNominations}
              disabled={selectedPrograms.size === 0}
              title={selectedPrograms.size === 0 ? "Please select at least one program to finalize" : "Finalize selected programs"}
            >
              Finalize Selected Programs ({selectedPrograms.size})
            </button>

            <button
              //onClick={downloadXLReport}
              className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            >
              Download XL Report
            </button>
          </div>
        </div>
      )}





      {selectedMonth && blendedPrograms.length === 0 && (
        <p className="text-center text-gray-700 mt-4">
          No Data available for the selected month.
        </p>
      )}
    </div>
  );
}

export default BlendedNominationFinalization;
